/** Import types. */
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    CashReserve,
    CashReserveInput,
    CashTranche,
    CashTrancheInput,
    Fee,
    FeeInput,
    SyntheticExcessSpread,
    SyntheticExcessSpreadInput,
    SyntheticTranche,
    SyntheticTrancheInput,
    TimeSeries,
    UUID,
    Variable,
    WaterfallItem,
    WaterfallItemInput
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {RBSCard, RBSCardVariants} from '../../base/rbs_components/RBSCard';
import {TabCardHeader} from './components/TabCardHeader';
import {
    BaseComponent,
    type BaseComponentState,
    BaseComponentStateInitial
} from "../../base/ui_general/BaseComponent.tsx";
import {createSuccessFeedbackMessage, formatEuro, formatPercent} from '../../base/utils/GeneralUtilities';
import {CashTrancheDetailModal} from "../modals/CashTrancheDetailModal.tsx";
import {SyntheticTrancheDetailModal} from "../modals/SyntheticTrancheDetailModal.tsx";
import {CashReserveDetailModal} from "../modals/CashReserveDetailModal.tsx";
import {SyntheticExcessSpreadDetailModal} from "../modals/SyntheticExcessSpreadDetailModal.tsx";
import {FeeDetailModal} from "../modals/FeeDetailModal.tsx";
import {InputStateControl} from '../../base/utils/InputStateControl';
import {RBSButton, RBSButtonSizes, RBSButtonVariants} from '../../base/rbs_components/RBSButton';
import {getRequest_create_cash_tranche, getRequest_update_cash_tranche} from '../../api/cash_tranche';
import {getRequest_create_synthetic_tranche, getRequest_update_synthetic_tranche} from '../../api/synthetic_tranche';
import {getRequest_create_cash_reserve, getRequest_update_cash_reserve} from '../../api/cash_reserve';
import {
    getRequest_create_synthetic_excess_spread,
    getRequest_update_synthetic_excess_spread
} from '../../api/synthetic_excess_spread';
import {getRequest_create_fee, getRequest_update_fee} from '../../api/fee';
import {ApiEndPoints, LabelTextMessageTypes, TransactionTypes} from '../../base/Settings';
import autobind from 'autobind-decorator';
import {getRequest_delete_waterfall_item, getRequest_update_waterfall_item} from "../../api/waterfall_item.tsx";
import styles from './TabShared.module.css';


/** Props for the LiabilitiesTab component. */
interface LiabilitiesTabProps extends DesignCenterTabProps {
    /** Array of cash tranches */
    cash_tranches: Array<CashTranche>;
    /** Array of synthetic tranches */
    synthetic_tranches: Array<SyntheticTranche>;
    /** Array of cash reserves */
    cash_reserves: Array<CashReserve>;
    /** Array of synthetic excess spreads */
    synthetic_excess_spreads: Array<SyntheticExcessSpread>;
    /** Array of fees */
    fees: Array<Fee>;
    /** Array of waterfall items for the transaction. */
    waterfall_items: Array<WaterfallItem> | undefined;
    /** Array of time series for the transaction (optional). */
    time_series: Array<TimeSeries> | undefined;
    /** Array of variables for the transaction (optional). */
    variables: Array<Variable> | undefined;
}

/** State for the LiabilitiesTab component. */
interface LiabilitiesTabState extends BaseComponentState {
    showCashTrancheModal: boolean;
    showSyntheticTrancheModal: boolean;
    showCashReserveModal: boolean;
    showSyntheticExcessSpreadModal: boolean;
    showFeeModal: boolean;
    cashTrancheModalValid: boolean;
    syntheticTrancheModalValid: boolean;
    cashReserveModalValid: boolean;
    syntheticExcessSpreadModalValid: boolean;
    feeModalValid: boolean;
    editingCashTranche: CashTranche | undefined;
    editingSyntheticTranche: SyntheticTranche | undefined;
    editingCashReserve: CashReserve | undefined;
    editingSyntheticExcessSpread: SyntheticExcessSpread | undefined;
    editingFee: Fee | undefined;
    hoveredCashTrancheRow: number | null;
    hoveredSyntheticTrancheRow: number | null;
    hoveredCashReserveRow: number | null;
    hoveredSyntheticExcessSpreadRow: number | null;
    hoveredFeeRow: number | null;
}

/**
 * Displays an overview of liabilities including cash tranches, synthetic tranches, cash reserves, synthetic excess spreads, and fees.
 */
export class LiabilitiesTab extends BaseComponent<LiabilitiesTabProps, LiabilitiesTabState> {
    private readonly cashTrancheInputStateControl: InputStateControl;
    private readonly syntheticTrancheInputStateControl: InputStateControl;
    private readonly cashReserveInputStateControl: InputStateControl;
    private readonly syntheticExcessSpreadInputStateControl: InputStateControl;
    private readonly feeInputStateControl: InputStateControl;

    constructor(props: LiabilitiesTabProps) {
        super(props);
        this.state = {
            ...BaseComponentStateInitial,
            showCashTrancheModal: false,
            showSyntheticTrancheModal: false,
            showCashReserveModal: false,
            showSyntheticExcessSpreadModal: false,
            showFeeModal: false,
            cashTrancheModalValid: true,
            syntheticTrancheModalValid: true,
            cashReserveModalValid: true,
            syntheticExcessSpreadModalValid: true,
            feeModalValid: true,
            editingCashTranche: undefined,
            editingSyntheticTranche: undefined,
            editingCashReserve: undefined,
            editingSyntheticExcessSpread: undefined,
            editingFee: undefined,
            hoveredCashTrancheRow: null,
            hoveredSyntheticTrancheRow: null,
            hoveredCashReserveRow: null,
            hoveredSyntheticExcessSpreadRow: null,
            hoveredFeeRow: null,
        };
        this.cashTrancheInputStateControl = new InputStateControl();
        this.syntheticTrancheInputStateControl = new InputStateControl();
        this.cashReserveInputStateControl = new InputStateControl();
        this.syntheticExcessSpreadInputStateControl = new InputStateControl();
        this.feeInputStateControl = new InputStateControl();
        this.cashTrancheInputStateControl.setGlobalValidityUpdateCallback(this.handleCashTrancheModalValidityUpdate);
        this.syntheticTrancheInputStateControl.setGlobalValidityUpdateCallback(this.handleSyntheticTrancheModalValidityUpdate);
        this.cashReserveInputStateControl.setGlobalValidityUpdateCallback(this.handleCashReserveModalValidityUpdate);
        this.syntheticExcessSpreadInputStateControl.setGlobalValidityUpdateCallback(this.handleSyntheticExcessSpreadModalValidityUpdate);
        this.feeInputStateControl.setGlobalValidityUpdateCallback(this.handleFeeModalValidityUpdate);
    }

    /**
     * Callback for CashTrancheDetailModal validity updates.
     * Updates cashTrancheModalValid in state.
     * @param isValid - Boolean indicating if the modal input is valid.
     */
    @autobind
    handleCashTrancheModalValidityUpdate(isValid: boolean) {
        this.setState({cashTrancheModalValid: isValid});
    }


    /**
     * Callback for SyntheticTrancheDetailModal validity updates.
     * Updates syntheticTrancheModalValid in state.
     * @param isValid - Boolean indicating if the modal input is valid.
     */
    @autobind
    handleSyntheticTrancheModalValidityUpdate(isValid: boolean) {
        this.setState({syntheticTrancheModalValid: isValid});
    }


    /**
     * Callback for CashReserveDetailModal validity updates.
     * Updates cashReserveModalValid in state.
     * @param isValid - Boolean indicating if the modal input is valid.
     */
    @autobind
    handleCashReserveModalValidityUpdate(isValid: boolean) {
        this.setState({cashReserveModalValid: isValid});
    }


    /**
     * Callback for SyntheticExcessSpreadDetailModal validity updates.
     * Updates syntheticExcessSpreadModalValid in state.
     * @param isValid - Boolean indicating if the modal input is valid.
     */
    @autobind
    handleSyntheticExcessSpreadModalValidityUpdate(isValid: boolean) {
        this.setState({syntheticExcessSpreadModalValid: isValid});
    }


    /**
     * Callback for FeeDetailModal validity updates.
     * Updates feeModalValid in state.
     * @param isValid - Boolean indicating if the modal input is valid.
     */
    @autobind
    handleFeeModalValidityUpdate(isValid: boolean) {
        this.setState({feeModalValid: isValid});
    }

    /**
     * Handles submit for CashTrancheDetailModal (create or update).
     * Uses logic analogous to handleSyntheticTrancheModalSubmit.
     */
    @autobind
    handleCashTrancheModalSubmit(cashTrancheInput: CashTrancheInput, waterfallItemInput: WaterfallItemInput) {
        if (this.props.transaction && this.props.transaction.id) {
            waterfallItemInput.transaction_id = this.props.transaction.id;
        }
        if (this.state.editingCashTranche) {
            // Update existing cash tranche
            const updatedTranche = {...this.state.editingCashTranche, ...cashTrancheInput};
            const updatedWaterfallItem = this.props.waterfall_items?.find(w => w.id === updatedTranche.waterfall_item_id);
            if (!updatedWaterfallItem) return;
            Object.assign(updatedWaterfallItem, waterfallItemInput);
            this.processFetchRequest(
                getRequest_update_cash_tranche(updatedTranche),
                () => {
                    this.processFetchRequest(
                        getRequest_update_waterfall_item(updatedWaterfallItem), // If needed, replace with correct function
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.CASH_TRANCHE_UPDATED));
                            this.props.loadData([ApiEndPoints.CASH_TRANCHE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                            this.setState({showCashTrancheModal: false, editingCashTranche: undefined});
                        }
                    );
                }
            );
        } else {
            // Create new cash tranche
            this.processFetchRequest(
                getRequest_create_cash_tranche(cashTrancheInput, waterfallItemInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.CASH_TRANCHE_UPDATED));
                    this.props.loadData([ApiEndPoints.CASH_TRANCHE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                    this.setState({showCashTrancheModal: false, editingCashTranche: undefined});
                }
            );
        }
    }

    /**
     * Handles submit for SyntheticTrancheDetailModal (create or update).
     * This logic is now taken over analogously to the other handle...submit functions.
     */
    @autobind
    handleSyntheticTrancheModalSubmit(syntheticTrancheInput: SyntheticTrancheInput, waterfallItemInput: WaterfallItemInput) {
        if (this.props.transaction && this.props.transaction.id) {
            waterfallItemInput.transaction_id = this.props.transaction.id;
        }
        if (this.state.editingSyntheticTranche) {
            // Update existing synthetic tranche
            const updatedTranche = {...this.state.editingSyntheticTranche, ...syntheticTrancheInput};
            const updatedWaterfallItem = this.props.waterfall_items?.find(w => w.id === updatedTranche.waterfall_item_id);
            if (!updatedWaterfallItem) return;
            Object.assign(updatedWaterfallItem, waterfallItemInput);
            this.processFetchRequest(
                getRequest_update_synthetic_tranche(updatedTranche),
                () => {
                    this.processFetchRequest(
                        getRequest_update_waterfall_item(updatedWaterfallItem),
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.SYNTHETIC_TRANCHE_UPDATED));
                            this.props.loadData([ApiEndPoints.SYNTHETIC_TRANCHE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                            this.setState({showSyntheticTrancheModal: false, editingSyntheticTranche: undefined});
                        }
                    );
                }
            );
        } else {
            // Create new synthetic tranche
            this.processFetchRequest(
                getRequest_create_synthetic_tranche(syntheticTrancheInput, waterfallItemInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.SYNTHETIC_TRANCHE_UPDATED));
                    this.props.loadData([ApiEndPoints.SYNTHETIC_TRANCHE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                    this.setState({showSyntheticTrancheModal: false, editingSyntheticTranche: undefined});
                }
            );
        }
    }


    /**
     * Handles submit for CashReserveDetailModal (create or update).
     * Uses logic analogous to handleSyntheticTrancheModalSubmit.
     */
    @autobind
    handleCashReserveModalSubmit(cashReserveInput: CashReserveInput, waterfallItemInput: WaterfallItemInput) {
        if (this.props.transaction && this.props.transaction.id) {
            waterfallItemInput.transaction_id = this.props.transaction.id;
        }
        if (this.state.editingCashReserve) {
            // Update existing cash reserve
            const updatedReserve = {...this.state.editingCashReserve, ...cashReserveInput};
            const updatedWaterfallItem = this.props.waterfall_items?.find(w => w.id === updatedReserve.waterfall_item_id);
            if (!updatedWaterfallItem) return;
            Object.assign(updatedWaterfallItem, waterfallItemInput);
            this.processFetchRequest(
                getRequest_update_cash_reserve(updatedReserve),
                () => {
                    this.processFetchRequest(
                        getRequest_update_waterfall_item(updatedWaterfallItem), // If needed, replace with correct function
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.CASH_RESERVE_UPDATED));
                            this.props.loadData([ApiEndPoints.CASH_RESERVE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                            this.setState({showCashReserveModal: false, editingCashReserve: undefined});
                        }
                    );
                }
            );
        } else {
            // Create new cash reserve
            this.processFetchRequest(
                getRequest_create_cash_reserve(cashReserveInput, waterfallItemInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.CASH_RESERVE_UPDATED));
                    this.props.loadData([ApiEndPoints.CASH_RESERVE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                    this.setState({showCashReserveModal: false, editingCashReserve: undefined});
                }
            );
        }
    }

    /**
     * Handles submit for SyntheticExcessSpreadDetailModal (create or update).
     * Uses logic analogous to handleSyntheticTrancheModalSubmit.
     */
    @autobind
    handleSyntheticExcessSpreadModalSubmit(syntheticExcessSpreadInput: SyntheticExcessSpreadInput, waterfallItemInput: WaterfallItemInput) {
        if (this.props.transaction && this.props.transaction.id) {
            waterfallItemInput.transaction_id = this.props.transaction.id;
        }
        if (this.state.editingSyntheticExcessSpread) {
            // Update existing synthetic excess spread
            const updatedSpread = {...this.state.editingSyntheticExcessSpread, ...syntheticExcessSpreadInput};
            const updatedWaterfallItem = this.props.waterfall_items?.find(w => w.id === updatedSpread.waterfall_item_id);
            if (!updatedWaterfallItem) return;
            Object.assign(updatedWaterfallItem, waterfallItemInput);
            this.processFetchRequest(
                getRequest_update_synthetic_excess_spread(updatedSpread),
                () => {
                    this.processFetchRequest(
                        getRequest_update_waterfall_item(updatedWaterfallItem), // If needed, replace with correct function
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.SYNTHETIC_EXCESS_SPREAD_UPDATED));
                            this.props.loadData([ApiEndPoints.SYNTHETIC_EXCESS_SPREAD, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                            this.setState({
                                showSyntheticExcessSpreadModal: false,
                                editingSyntheticExcessSpread: undefined
                            });
                        }
                    );
                }
            );
        } else {
            // Create new synthetic excess spread
            this.processFetchRequest(
                getRequest_create_synthetic_excess_spread(syntheticExcessSpreadInput, waterfallItemInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.SYNTHETIC_EXCESS_SPREAD_UPDATED));
                    this.props.loadData([ApiEndPoints.SYNTHETIC_EXCESS_SPREAD, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                    this.setState({showSyntheticExcessSpreadModal: false, editingSyntheticExcessSpread: undefined});
                }
            );
        }
    }

    /**
     * Handles submit for FeeDetailModal (create or update).
     * Uses logic analogous to handleSyntheticTrancheModalSubmit.
     */
    @autobind
    handleFeeModalSubmit(feeInput: FeeInput, waterfallItemInput: WaterfallItemInput) {
        if (this.props.transaction && this.props.transaction.id) {
            waterfallItemInput.transaction_id = this.props.transaction.id;
        }
        if (this.state.editingFee) {
            // Update existing fee
            const updatedFee = {...this.state.editingFee, ...feeInput};
            const updatedWaterfallItem = this.props.waterfall_items?.find(w => w.id === updatedFee.waterfall_item_id);
            if (!updatedWaterfallItem) return;
            Object.assign(updatedWaterfallItem, waterfallItemInput);
            this.processFetchRequest(
                getRequest_update_fee(updatedFee),
                () => {
                    this.processFetchRequest(
                        getRequest_update_waterfall_item(updatedWaterfallItem), // If needed, replace with correct function
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FEE_UPDATED));
                            this.props.loadData([ApiEndPoints.FEE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                            this.setState({showFeeModal: false, editingFee: undefined});
                        }
                    );
                }
            );
        } else {
            // Create new fee
            this.processFetchRequest(
                getRequest_create_fee(feeInput, waterfallItemInput),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.FEE_UPDATED));
                    this.props.loadData([ApiEndPoints.FEE, ApiEndPoints.WATERFALL_ITEM, ApiEndPoints.VARIABLE]);
                    this.setState({showFeeModal: false, editingFee: undefined});
                }
            );
        }
    }

    /**
     * Handles the deletion of a waterfall item by its UUID via an API request.
     * @param waterfall_item_id The UUID of the waterfall item to delete.
     */
    @autobind
    handleDeleteWaterfallItem(waterfall_item_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_waterfall_item(waterfall_item_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.WATERFALL_ITEM_DELETED));
                this.props.loadData([
                    ApiEndPoints.WATERFALL_ITEM,
                    ApiEndPoints.CASH_TRANCHE,
                    ApiEndPoints.CASH_RESERVE,
                    ApiEndPoints.FEE,
                    ApiEndPoints.SYNTHETIC_TRANCHE,
                    ApiEndPoints.SYNTHETIC_EXCESS_SPREAD,
                    ApiEndPoints.VARIABLE
                ]);
            }
        );
    }

    render() {
        /**
         * Type helper to extract keys of T whose values are numbers.
         */
        type NumericKeys<T> = {
            [K in keyof T]: T[K] extends number ? K : never;
        }[keyof T];

        const sumField = <T, >(arr: T[], field: NumericKeys<T>): number =>
            arr.reduce((sum, item) => {
                const value = item[field];
                return sum + (typeof value === 'number' ? value : 0);
            }, 0);


        // Helper to get waterfall item name
        const getWaterfallName = (id: string | undefined) => {
            if (!id || !this.props.waterfall_items) return "-";
            const item = this.props.waterfall_items.find(w => w.id === id);
            return item ? item.name : "-";
        };

        // Helper to get time series name from base_curve_id
        const getTimeSeriesName = (id: string | undefined) => {
            if (!id || !this.props.time_series) return "---";
            const ts = this.props.time_series.find(t => t.id === id);
            return ts ? ts.name : "---";
        };

        // Utility to get variable name by id
        const getVariableName = (variable_id?: string): string => {
            if (!variable_id) return '';
            const variables = (this.props.variables ?? []);
            const v = variables.find((vv: Variable) => vv.id === variable_id);
            return v ? v.name : '';
        }

        // Helper to compute weighted average
        function weightedAverage<T>(arr: Array<T>, valueKey: keyof T, weightKey: keyof T): number {
            let weightedSum = 0;
            let totalWeight = 0;
            for (const item of arr) {
                const value = typeof item[valueKey] === 'number' ? (item[valueKey] as number) : 0;
                const weight = typeof item[weightKey] === 'number' ? (item[weightKey] as number) : 0;
                weightedSum += value * weight;
                totalWeight += weight;
            }
            return totalWeight > 0 ? weightedSum / totalWeight : 0;
        }

        const transactionType = this.props.transaction?.transaction_type;
        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    <div className={styles.cardStack}>
                        {/* Cash Tranches */}
                        {transactionType === TransactionTypes.CASH && (
                            <RBSCard
                                header={<TabCardHeader
                                    icon={"💵"}
                                    title={<span className={styles.cardHeaderTitle}>Cash Tranches</span>}
                                    actions={
                                        <RBSButton
                                            label="Add Row"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({
                                                showCashTrancheModal: true,
                                                editingCashTranche: undefined
                                            })}
                                        />
                                    }
                                />}
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Name</th>
                                            <th className={styles.headerPadLg}>Initial Nominal</th>
                                            <th className={styles.headerPadLg}>Spread</th>
                                            <th className={styles.headerPadLg}>Base Curve</th>
                                            <th className={styles.headerPadLg}>Default Definition</th>
                                            <th className={styles.headerPadLg}>Invested</th>
                                            <th className={styles.headerPadLg}>Target ROE</th>
                                            <th className={styles.headerPadLg}>Target Amortization</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {this.props.cash_tranches.map((tranche, idx) => (
                                            <tr
                                                key={idx}
                                                className={this.state.hoveredCashTrancheRow === idx ? styles.tableRowHover : ''}
                                                onMouseEnter={() => this.setState({hoveredCashTrancheRow: idx})}
                                                onMouseLeave={() => this.setState({hoveredCashTrancheRow: null})}
                                            >
                                                <td className={`${styles.cellPadLg}`}>{getWaterfallName(tranche.waterfall_item_id)}</td>
                                                <td className={`${styles.cellPadLg}`}>{formatEuro(tranche.initial_nominal)}</td>
                                                <td className={`${styles.cellPadLg}`}>{formatPercent(tranche.spread)}</td>
                                                <td className={`${styles.cellPadLg}`}>{getTimeSeriesName(tranche.base_curve_id)}</td>
                                                <td className={`${styles.cellPadLg}`}>{tranche.default_definition}</td>
                                                <td className={`${styles.cellPadLg}`}>{tranche.invested ? "Yes" : "No"}</td>
                                                <td className={`${styles.cellPadLg}`}>{formatPercent(tranche.target_return_on_equity)}</td>
                                                <td className={`${styles.cellPadLg}`}>{getVariableName(tranche.target_amortization_variable_id)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            onClick={() => this.setState({
                                                                showCashTrancheModal: true,
                                                                editingCashTranche: tranche
                                                            })}
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            onClick={() => this.handleDeleteWaterfallItem(tranche.waterfall_item_id)}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>Total</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatEuro(sumField(this.props.cash_tranches, 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(weightedAverage(this.props.cash_tranches, 'spread', 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(weightedAverage(this.props.cash_tranches, 'target_return_on_equity', 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.actionCell} ${styles.totalRowCell}`}></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        )}

                        {/* Synthetic Tranches */}
                        {transactionType === TransactionTypes.SYNTHETIC && (
                            <RBSCard
                                header={<TabCardHeader
                                    icon={"💸"}
                                    title={<span className={styles.cardHeaderTitle}>Synthetic Tranches</span>}
                                    actions={
                                        <RBSButton
                                            label="Add Row"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({
                                                showSyntheticTrancheModal: true,
                                                editingSyntheticTranche: undefined
                                            })}
                                        />
                                    }
                                />}
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Name</th>
                                            <th className={styles.headerPadLg}>Initial Nominal</th>
                                            <th className={styles.headerPadLg}>Spread</th>
                                            <th className={styles.headerPadLg}>Base Curve</th>
                                            <th className={styles.headerPadLg}>Default Definition</th>
                                            <th className={styles.headerPadLg}>Invested</th>
                                            <th className={styles.headerPadLg}>Target ROE</th>
                                            <th className={styles.headerPadLg}>Target Amortization</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {this.props.synthetic_tranches.map((tranche, idx) => (
                                            <tr
                                                key={idx}
                                                className={this.state.hoveredSyntheticTrancheRow === idx ? styles.tableRowHover : ''}
                                                onMouseEnter={() => this.setState({hoveredSyntheticTrancheRow: idx})}
                                                onMouseLeave={() => this.setState({hoveredSyntheticTrancheRow: null})}
                                            >
                                                <td className={styles.cellPadLg}>{getWaterfallName(tranche.waterfall_item_id)}</td>
                                                <td className={styles.cellPadLg}>{formatEuro(tranche.initial_nominal)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(tranche.spread)}</td>
                                                <td className={styles.cellPadLg}>{getTimeSeriesName(tranche.base_curve_id)}</td>
                                                <td className={styles.cellPadLg}>{tranche.default_definition}</td>
                                                <td className={styles.cellPadLg}>{tranche.invested ? "Yes" : "No"}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(tranche.target_return_on_equity)}</td>
                                                <td className={styles.cellPadLg}>{getVariableName(tranche.target_amortization_variable_id)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            onClick={() => this.setState({
                                                                showSyntheticTrancheModal: true,
                                                                editingSyntheticTranche: tranche
                                                            })}
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            onClick={() => this.handleDeleteWaterfallItem(tranche.waterfall_item_id)}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>Total</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatEuro(sumField(this.props.synthetic_tranches, 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(weightedAverage(this.props.synthetic_tranches, 'spread', 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(weightedAverage(this.props.synthetic_tranches, 'target_return_on_equity', 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        )}

                        {/* Cash Reserves */}
                        {transactionType === TransactionTypes.CASH && (
                            <RBSCard
                                header={<TabCardHeader
                                    icon={"🏦"}
                                    title={<span className={styles.cardHeaderTitle}>Cash Reserve</span>}
                                    actions={
                                        <RBSButton
                                            label="Add Row"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({
                                                showCashReserveModal: true,
                                                editingCashReserve: undefined
                                            })}
                                        />
                                    }
                                />}
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Name</th>
                                            <th className={styles.headerPadLg}>Initial Nominal</th>
                                            <th className={styles.headerPadLg}>Reserve Rate</th>
                                            <th className={styles.headerPadLg}>Reference Variable</th>
                                            <th className={styles.headerPadLg}>Spread Receiving</th>
                                            <th className={styles.headerPadLg}>Spread Paying</th>
                                            <th className={styles.headerPadLg}>Base Curve</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {this.props.cash_reserves.map((reserve, idx) => (
                                            <tr
                                                key={idx}
                                                className={this.state.hoveredCashReserveRow === idx ? styles.tableRowHover : ''}
                                                onMouseEnter={() => this.setState({hoveredCashReserveRow: idx})}
                                                onMouseLeave={() => this.setState({hoveredCashReserveRow: null})}
                                            >
                                                <td className={styles.cellPadLg}>{getWaterfallName(reserve.waterfall_item_id)}</td>
                                                <td className={styles.cellPadLg}>{formatEuro(reserve.initial_nominal)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(reserve.reserve_rate)}</td>
                                                <td className={styles.cellPadLg}>{getVariableName(reserve.reference_variable_id)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(reserve.spread_receiving)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(reserve.spread_paying)}</td>
                                                <td className={styles.cellPadLg}>{getTimeSeriesName(reserve.base_curve_id)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            onClick={() => this.setState({
                                                                showCashReserveModal: true,
                                                                editingCashReserve: reserve
                                                            })}
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            onClick={() => this.handleDeleteWaterfallItem(reserve.waterfall_item_id)}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>Total</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatEuro(sumField(this.props.cash_reserves, 'initial_nominal'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(sumField(this.props.cash_reserves, 'reserve_rate'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(sumField(this.props.cash_reserves, 'spread_receiving'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(sumField(this.props.cash_reserves, 'spread_paying'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        )}

                        {/* Synthetic Excess Spreads */}
                        {transactionType === TransactionTypes.SYNTHETIC && (
                            <RBSCard
                                header={<TabCardHeader
                                    icon={"📈"}
                                    title={<span className={styles.cardHeaderTitle}>Synthetic Excess Spread</span>}
                                    actions={
                                        <RBSButton
                                            label="Add Row"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({
                                                showSyntheticExcessSpreadModal: true,
                                                editingSyntheticExcessSpread: undefined
                                            })}
                                        />
                                    }
                                />}
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Name</th>
                                            <th className={styles.headerPadLg}>Value</th>
                                            <th className={styles.headerPadLg}>Reference Variable</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {this.props.synthetic_excess_spreads.map((spread, idx) => (
                                            <tr
                                                key={idx}
                                                className={this.state.hoveredSyntheticExcessSpreadRow === idx ? styles.tableRowHover : ""}
                                                onMouseEnter={() => this.setState({hoveredSyntheticExcessSpreadRow: idx})}
                                                onMouseLeave={() => this.setState({hoveredSyntheticExcessSpreadRow: null})}
                                            >
                                                <td className={styles.cellPadLg}>{getWaterfallName(spread.waterfall_item_id)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(spread.excess_spread)}</td>
                                                <td className={styles.cellPadLg}>{getVariableName(spread.reference_variable_id)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            onClick={() => this.setState({
                                                                showSyntheticExcessSpreadModal: true,
                                                                editingSyntheticExcessSpread: spread
                                                            })}
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            onClick={() => this.handleDeleteWaterfallItem(spread.waterfall_item_id)}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>Total</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(sumField(this.props.synthetic_excess_spreads, 'excess_spread'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        )}

                        {/* Fees */}
                        {transactionType === TransactionTypes.CASH && (
                            <RBSCard
                                header={<TabCardHeader
                                    icon={"💳"}
                                    title={<span className={styles.cardHeaderTitle}>Fees</span>}
                                    actions={
                                        <RBSButton
                                            label="Add Row"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({
                                                showFeeModal: true,
                                                editingFee: undefined
                                            })}
                                        />
                                    }
                                />}
                                header_class_name={styles.cardHeaderPrimary}
                                background={RBSCardVariants.Light}
                                border={RBSCardVariants.Primary}
                                body_class_name={styles.cardBodyPanel}
                                container_class_name={styles.roundedCard}
                            >
                                <div className={styles.tableScrollX}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={styles.headerPadLg}>Name</th>
                                            <th className={styles.headerPadLg}>Fixed Amount</th>
                                            <th className={styles.headerPadLg}>Variable Rate</th>
                                            <th className={styles.headerPadLg}>Reference Variable</th>
                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {this.props.fees.map((fee, idx) => (
                                            <tr
                                                key={idx}
                                                className={this.state.hoveredFeeRow === idx ? styles.tableRowHover : ""}
                                                onMouseEnter={() => this.setState({hoveredFeeRow: idx})}
                                                onMouseLeave={() => this.setState({hoveredFeeRow: null})}
                                            >
                                                <td className={styles.cellPadLg}>{getWaterfallName(fee.waterfall_item_id)}</td>
                                                <td className={styles.cellPadLg}>{formatEuro(fee.fix_amount)}</td>
                                                <td className={styles.cellPadLg}>{formatPercent(fee.variable_rate)}</td>
                                                <td className={styles.cellPadLg}>{getVariableName(fee.reference_variable_id)}</td>
                                                <td className={styles.actionCell}>
                                                    <div className={styles.hoverButtons}>
                                                        <RBSButton
                                                            label="✏️"
                                                            onClick={() => this.setState({
                                                                showFeeModal: true,
                                                                editingFee: fee
                                                            })}
                                                            variant={RBSButtonVariants.SECONDARY}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                        <RBSButton
                                                            label="🗑️"
                                                            onClick={() => this.handleDeleteWaterfallItem(fee.waterfall_item_id)}
                                                            variant={RBSButtonVariants.DANGER}
                                                            size={RBSButtonSizes.SMALL}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>Total</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatEuro(sumField(this.props.fees, 'fix_amount'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}>{formatPercent(sumField(this.props.fees, 'variable_rate'))}</td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`}></td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </RBSCard>
                        )}
                    </div>

                    <CashTrancheDetailModal
                        show={this.state.showCashTrancheModal}
                        onClose={() => this.setState({showCashTrancheModal: false, editingCashTranche: undefined})}
                        onSubmit={this.handleCashTrancheModalSubmit}
                        cashTranche={this.state.editingCashTranche}
                        waterfall_item={this.state.editingCashTranche && this.props.waterfall_items ? this.props.waterfall_items.find(w => w.id === this.state.editingCashTranche!.waterfall_item_id) : undefined}
                        isValid={this.state.cashTrancheModalValid}
                        inputStateControl={this.cashTrancheInputStateControl}
                        user_language={this.props.user_language}
                        time_series={this.props.time_series}
                        variables={this.props.variables}
                    />
                    <SyntheticTrancheDetailModal
                        show={this.state.showSyntheticTrancheModal}
                        onClose={() => this.setState({
                            showSyntheticTrancheModal: false,
                            editingSyntheticTranche: undefined
                        })}
                        onSubmit={this.handleSyntheticTrancheModalSubmit}
                        syntheticTranche={this.state.editingSyntheticTranche}
                        waterfall_item={this.state.editingSyntheticTranche && this.props.waterfall_items ? this.props.waterfall_items.find(w => w.id === this.state.editingSyntheticTranche!.waterfall_item_id) : undefined}
                        isValid={this.state.syntheticTrancheModalValid}
                        inputStateControl={this.syntheticTrancheInputStateControl}
                        user_language={this.props.user_language}
                        time_series={this.props.time_series}
                        variables={this.props.variables}
                    />
                    <CashReserveDetailModal
                        show={this.state.showCashReserveModal}
                        onClose={() => this.setState({showCashReserveModal: false, editingCashReserve: undefined})}
                        onSubmit={this.handleCashReserveModalSubmit}
                        cashReserve={this.state.editingCashReserve}
                        waterfall_item={this.state.editingCashReserve && this.props.waterfall_items ? this.props.waterfall_items.find(w => w.id === this.state.editingCashReserve!.waterfall_item_id) : undefined}
                        isValid={this.state.cashReserveModalValid}
                        inputStateControl={this.cashReserveInputStateControl}
                        user_language={this.props.user_language}
                        time_series={this.props.time_series}
                        variables={this.props.variables}
                    />
                    <SyntheticExcessSpreadDetailModal
                        show={this.state.showSyntheticExcessSpreadModal}
                        onClose={() => this.setState({
                            showSyntheticExcessSpreadModal: false,
                            editingSyntheticExcessSpread: undefined
                        })}
                        onSubmit={this.handleSyntheticExcessSpreadModalSubmit}
                        syntheticExcessSpread={this.state.editingSyntheticExcessSpread}
                        waterfall_item={this.state.editingSyntheticExcessSpread && this.props.waterfall_items ? this.props.waterfall_items.find(w => w.id === this.state.editingSyntheticExcessSpread!.waterfall_item_id) : undefined}
                        isValid={this.state.syntheticExcessSpreadModalValid}
                        inputStateControl={this.syntheticExcessSpreadInputStateControl}
                        user_language={this.props.user_language}
                        variables={this.props.variables}
                    />
                    <FeeDetailModal
                        show={this.state.showFeeModal}
                        onClose={() => this.setState({showFeeModal: false, editingFee: undefined})}
                        onSubmit={this.handleFeeModalSubmit}
                        fee={this.state.editingFee}
                        waterfall_item={this.state.editingFee && this.props.waterfall_items ? this.props.waterfall_items.find(w => w.id === this.state.editingFee!.waterfall_item_id) : undefined}
                        isValid={this.state.feeModalValid}
                        inputStateControl={this.feeInputStateControl}
                        user_language={this.props.user_language}
                        variables={this.props.variables}
                    />
                </div>
            </div>
        );
    }
}
